import { getUndoSignature } from "../utils/autosaveDecision.es6";
import { isStandaloneWindow, shouldGuardNavigation } from "../utils/unsavedChangesGuard.es6";

// Wires the browser-back (pushState/popstate) trap and a dynamically
// added/removed beforeunload listener around the shared unsaved-changes
// predicate (unsavedChangesGuard.es6). No-ops entirely when embedded in an
// iframe — see that file's shouldGuardNavigation doc for why.
export const createUnsavedChangesGuard = ({
  store,
  getTrainingId,
  getLastSavedSignature,
  onBlockedBack,
}) => {
  let attached = false; // true once addListener has pushed the sentinel + wired listeners
  let beforeUnloadAttached = false;
  let unsubscribeStore = null;

  const currentSignature = () => getUndoSignature(store.getState().undoGroup);

  const guardActive = () =>
    shouldGuardNavigation({
      trainingId: getTrainingId(),
      isStandalone: isStandaloneWindow(),
      lastSavedSignature: getLastSavedSignature(),
      currentSignature: currentSignature(),
    });

  const handleBeforeUnload = (ev) => {
    if (!guardActive()) return;
    ev.preventDefault();
    ev.returnValue = "";
  };

  const syncBeforeUnload = () => {
    const active = guardActive();
    if (active && !beforeUnloadAttached) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      beforeUnloadAttached = true;
    } else if (!active && beforeUnloadAttached) {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      beforeUnloadAttached = false;
    }
  };

  const pushSentinel = () => {
    window.history.pushState({ __unsavedChangesGuard: true }, "", window.location.href);
  };

  const handlePopState = () => {
    if (!guardActive()) return;
    pushSentinel(); // undo the browser's back-step so the URL doesn't move
    onBlockedBack();
  };

  const detach = () => {
    window.removeEventListener("popstate", handlePopState);
    if (beforeUnloadAttached) {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      beforeUnloadAttached = false;
    }
    if (unsubscribeStore) {
      unsubscribeStore();
      unsubscribeStore = null;
    }
  };

  const addListener = () => {
    if (typeof window === "undefined" || !isStandaloneWindow()) return; // embedded: no-op by design
    pushSentinel();
    window.addEventListener("popstate", handlePopState);
    attached = true;
    syncBeforeUnload();
    unsubscribeStore = store.subscribe(syncBeforeUnload);
  };

  // Real unmount (editor closing): drop listeners and pop the sentinel
  // entry pushed on mount, so a later, unrelated Back press isn't stuck
  // behind a dead duplicate URL.
  const removeListener = () => {
    if (!attached) return;
    detach();
    attached = false;
    window.history.back();
  };

  // User confirmed leaving (Save and leave / Leave without saving): drop
  // listeners first (no re-trap on the navigation about to happen), then
  // consume both the sentinel and the editor's own entry so the browser
  // lands on whatever the user actually pressed Back to reach.
  const confirmLeave = () => {
    detach();
    attached = false;
    window.history.go(-2);
  };

  return { addListener, removeListener, confirmLeave };
};
